import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookUser,
  UserCheck,
  Shield,
  Briefcase,
  TreePalm,
  Laptop,
  Signature,
  Vault,
  Plus,
  Handshake,
  Calendar,
  Search,
} from "lucide-react";
import * as helperService from "../../services/helperService";
import { employeeService } from "../../services/employeeService";

const HROverview = ({ userPermission }) => {
  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [activeFilter, setActiveFilter] = useState({ key: null, value: null });
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await employeeService.getEmployees("", 200, 0);
        const flat = (res.items || []).map((item) => ({
          id: item.id,
          name: item.employee?.name ?? "",
          role: item.employee?.job_title ?? "",
          department: item.employee?.department ?? "",
          email: item.employee?.work_email ?? "",
          status: item.employee?.status ?? "",
          is_hr_manager: item.employee?.is_hr_manager ?? "",
          manager_id: item.employee?.manager_id ?? null,
          level: item.employee?.level ?? "",
        }));
        setEmployees(flat);
        setTotal(res.total ?? flat.length);
      } catch (err) {
        console.error("Failed to load employees", err);
      }
    })();
  }, []);

  const stats = useMemo(() => {
    return [
      {
        label: "Total Employees",
        value: total,
        helper: "All employees",
        icon: BookUser,
        helperClass: "text-canopy-blue",
        iconClass: "text-canopy-blue",
        filterKey: null,
        filterValue: null,
      },
      {
        label: "Active",
        value: employees.filter((e) => e.status === "Active").length,
        helper: "Currently active",
        icon: UserCheck,
        helperClass: "text-green-600",
        iconClass: "text-green-600",
        filterKey: "status",
        filterValue: "Active",
      },
      {
        label: "HR Managers",
        value: employees.filter((e) => e.is_hr_manager === "Yes").length,
        helper: "All HR managers",
        icon: Shield,
        helperClass: "text-red-600",
        iconClass: "text-red-600",
        filterKey: "is_hr_manager",
        filterValue: "Yes",
      },
      {
        label: "Managers",
        value: employees.filter((e) => e.level === "Manager").length,
        helper: "All managers",
        icon: Briefcase,
        helperClass: "text-canopy-blue",
        iconClass: "text-canopy-blue",
        filterKey: "level",
        filterValue: "Manager",
      },
      {
        label: "Partners",
        value: employees.filter((e) => e.level === "Partner").length,
        helper: "All partners",
        icon: Handshake,
        helperClass: "text-orange-600",
        iconClass: "text-orange-600",
        filterKey: "level",
        filterValue: "Partner",
      },
    ];
  }, [employees, total]);

  const filteredEmployees = useMemo(() => {
    let result = employees;
    if (activeFilter.key) {
      result = result.filter((e) => e[activeFilter.key] === activeFilter.value);
    }
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      result = result.filter((e) =>
        [e.name, e.email, e.role, e.department]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(term)),
      );
    }
    return result;
  }, [employees, activeFilter, searchTerm]);

  const handleStatClick = (filterKey, filterValue) => {
    if (
      !filterKey ||
      (activeFilter.key === filterKey && activeFilter.value === filterValue)
    ) {
      setActiveFilter({ key: null, value: null });
    } else {
      setActiveFilter({ key: filterKey, value: filterValue });
    }
  };

  const quickActions = [
    {
      label: "Contracts",
      icon: Signature,
      to: "/contracts",
      iconClass: "text-red-600",
    },
    {
      label: "Equipment",
      icon: Laptop,
      to: "/equipment",
      iconClass: "text-orange-500",
    },
    {
      label: "Reimbursements",
      icon: Vault,
      to: "/reimbursements",
      iconClass: "text-indigo-500",
    },
    {
      label: "Vacations",
      icon: TreePalm,
      to: "/vacations",
      iconClass: "text-green-600",
    },
    {
      label: "Calendar",
      icon: Calendar,
      to: "/calendars",
      iconClass: "text-yellow-600",
    },
  ];

  // const isAdminView = userPermission === "masterAccess";

  return (
    <div className="space-y-6 animate-fadeIn">
      <section className="bg-white border border-canopy-border rounded-xl p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
          {stats.map((item) => {
            const Icon = item.icon;
            const isActive =
              item.filterKey === null
                ? activeFilter.key === null
                : activeFilter.key === item.filterKey &&
                  activeFilter.value === item.filterValue;
            return (
              <button
                key={item.label}
                type="button"
                onClick={() =>
                  handleStatClick(item.filterKey, item.filterValue)
                }
                className={`text-left border rounded-xl p-5 bg-white transition-colors ${
                  isActive
                    ? "border-canopy-blue ring-2 ring-canopy-blue/10"
                    : "border-canopy-border hover:border-canopy-blue/40"
                }`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-canopy-text">
                    {item.label}
                  </h3>
                  <Icon className={`h-4 w-4 ${item.iconClass}`} />
                </div>
                <p className="text-4xl font-bold text-canopy-text mt-3">
                  {item.value}
                </p>
                <p className={`text-sm mt-2 ${item.helperClass}`}>
                  {item.helper}
                </p>
              </button>
            );
          })}
        </div>
      </section>

      <section className="bg-white border border-canopy-border rounded-xl p-6">
        <h2 className="text-4xl/[1.1] sm:text-4xl font-bold text-canopy-text">
          Management
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mt-6">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <Link
                key={action.label}
                to={action.to}
                className="border border-canopy-border rounded-xl px-4 py-6 bg-white hover:bg-gray-50 transition-colors text-center"
              >
                <Icon className={`h-5 w-5 mx-auto mb-2 ${action.iconClass}`} />
                <span className="font-semibold text-canopy-text">
                  {action.label}
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="bg-white border border-canopy-border rounded-xl p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <h2 className="text-4xl/[1.1] sm:text-4xl font-bold text-canopy-text">
            Employee Directory
          </h2>

          <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
            <div className="flex items-center border border-canopy-border rounded-lg px-3 py-2 w-full md:w-80">
              <Search className="h-4 w-4 text-canopy-textMuted mr-2" />
              <input
                type="text"
                placeholder="Search employees"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 outline-none"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  ✕
                </button>
              )}
            </div>

            <Link
              to="/create_employee"
              className="canopy-btn-primary inline-flex items-center justify-center gap-2 w-full md:w-auto"
            >
              <Plus className="w-6 h-6" />
            </Link>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {filteredEmployees.map((employee) => (
            <div
              key={employee.id}
              className="border border-canopy-border rounded-xl p-5 bg-white"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-full bg-[#0e1a66] text-white flex items-center justify-center font-semibold">
                    {helperService.getInitials(employee.name)}
                  </div>

                  <div>
                    <Link
                      to={`/employee/${employee.id}`}
                      state={{ from: "/human_resources" }}
                      className="text-2xl font-bold text-canopy-text hover:text-canopy-blue"
                    >
                      {employee.name}
                    </Link>
                    <p className="text-xl text-canopy-textMuted">
                      {employee.job_title} • {employee.email}
                    </p>
                    <p className="text-sm text-canopy-textMuted mt-1">
                      {employee.department} • {employee.level}
                    </p>
                  </div>
                </div>

                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                    employee.status === "Active"
                      ? "bg-green-100 text-green-700"
                      : employee.status === "Terminated"
                        ? "bg-red-100 text-red-700"
                        : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {employee.status || "—"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default HROverview;
